import { useRef } from 'react';
import type { TourneyServerToClient } from '@tg/shared';
import { paths } from '@/app/paths';
import { fx, haptic, useCelebration, wait } from '@/fx';
import { useLocale } from '@/i18n/useLocale';
import { toast } from '@/stores/toasts';
import { useSocketEvent } from '@/socket/socket';

type SeatEvent = Parameters<TourneyServerToClient['tourney:seat']>[0];
type ResultEvent = Parameters<TourneyServerToClient['tourney:result']>[0];

/**
 * Global listener of table tournaments (mounted once in the app chrome):
 *  - `tourney:seat` → «Your table is ready» with a link to the table, on any page; sound `tourney/seat-ready`, or
 *    `tourney/stage-up` when the seat is in a later stage than the last one seen (the player went through);
 *  - `tourney:result` → the place (and the prize); a prize bigger than the buy-in gets confetti, skippable with a
 *    tap; a smaller prize (a net loss) is a quiet notice.
 */
export function TourneyListener() {
  const l = useLocale();
  const cel = useCelebration();
  /** tourneyId → the last stage a seat came for (a repeated seat of the same stage — a restored table — is not a step up) */
  const stages = useRef(new Map<string, number>());

  useSocketEvent<[SeatEvent]>('tourney:seat', (e) => {
    const up = e.stage > (stages.current.get(e.tourneyId) ?? 1);
    stages.current.set(e.tourneyId, Math.max(e.stage, stages.current.get(e.tourneyId) ?? 1));
    toast.link(
      'tourney.seatToast',
      { to: paths.tourneyTable(l, e.game, e.tableId), labelId: 'tourney.seatGo' },
      { stage: e.stage },
      undefined,
      { sound: up ? 'tourney/stage-up' : 'tourney/seat-ready' },
    );
  });

  useSocketEvent<[ResultEvent]>('tourney:result', (e) => {
    if (e.prize > e.buyIn) {
      toast.success('tourney.resultPrize', { title: e.title, place: e.place, amount: e.prize });
      void cel.run(async (signal) => {
        fx.confetti();
        haptic('medium');
        await wait(3500, signal);
      });
    } else if (e.prize > 0) {
      // a prize below the buy-in is a loss: shown quietly, without a sign, no confetti (honest play)
      toast.notice('tourney.resultPrizeQuiet', undefined, { title: e.title, place: e.place, amount: e.prize });
    } else toast.notice('tourney.resultPlace', undefined, { title: e.title, place: e.place });
  });

  return null;
}
